import { useMemo, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import HighlightBox from "@ui-kit/layout/HighlightBox/HighlightBox.web";
import Button from "@ui-kit/form/Button/Button.web";
import {
  SeatingSelectReturnType,
  SeatWithLocationDataType,
} from "@shared/modules/seating/types/seating-types";
import WaitlistSelect from "./WaitlistSelect/WaitlistSelect.web";
import { useWaitlistSelectModal } from "./useWaitlistSelectModal";
import {
  MapItemType,
  WaitlistSelectModalProps,
} from "./WaitlistSelectModal.props";

const WaitlistSelectModal = (props: WaitlistSelectModalProps) => {
  const { resolve, isOpen, itemsOnSeat } = props;
  const { t } = useTranslation("seating");

  const [multiItemSelections, setMultiItemSelections] = useState<
    Array<SeatingSelectReturnType>
  >([]);

  const updateMultiItemSelections = (input: Array<SeatingSelectReturnType>) => {
    setMultiItemSelections(input);
  };

  const {
    cancelModal,
    selectItemPermission,
    getCurrentSelectedAmount,
    isTableSeat,
    itemTypes,
    isSeat,
  } = useWaitlistSelectModal({
    ...props,
    multiItemSelections,
    updateMultiItemSelections,
  });

  const headerText = useMemo(
    () =>
      isSeat
        ? t("Waitlisted Seat Selection")
        : `${t("Location")} ${itemsOnSeat?.$data?.identifier}`,
    [isSeat, itemsOnSeat]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancelModal}
      size="3xl"
      isCentered
      scrollBehavior="inside"
    >
      <Modal.Header alignItems="center">{headerText}</Modal.Header>
      <Modal.Body>
        <Flex
          data-testid="multi-item-waitlist-select-modal"
          w="full"
          direction="column"
          gap="md"
          mt="none"
        >
          {isSeat && !isTableSeat && (
            <HighlightBox statusColor="red" fontSize="md">
              {t(
                "Adding waitlisted seats to the cart does not reserve an exact seat or row number. Instead, you will be waitlisted for any seats that are released in this section (Section {{section_name}}).",
                {
                  section_name: (itemsOnSeat?.$data as SeatWithLocationDataType)
                    .segment_identifier,
                }
              )}
            </HighlightBox>
          )}
          {isTableSeat && (
            <Text fontSize="md">
              {t("Waitlists are not enabled for table seats.")}
            </Text>
          )}
          {!isTableSeat &&
            itemTypes.map((item: MapItemType) => (
              <WaitlistSelect
                key={item.id}
                item={item}
                selectItemPermission={selectItemPermission}
                getCurrentSelectedAmount={getCurrentSelectedAmount}
                {...props}
              />
            ))}
        </Flex>
      </Modal.Body>
      <Modal.Footer
        borderTop="1px solid"
        borderColor="gray.400"
        flexDirection="row"
        justifyContent="flex-end"
      >
        <Button
          data-testid="waitlist-select-continue"
          minWidth="element.7xl"
          variant="solid"
          colorPalette="primary"
          onClick={() => resolve(multiItemSelections)}
        >
          {t("Continue", { ns: "common" })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WaitlistSelectModal;
